import { ltjsProperty, ltjsDestructor } from '../ltjs-decorators';
import { ReportSession } from '@sas-dvr/ltjs-vav';
import { createStoreWithBirdMiddleware } from '../bird';
import { filterNulls } from '../bird/util';
import { BirdState } from '@sas-dvr/internal-va-react-core/store/bird/birdState';
import {
  ViewSet,
  ReportController,
  PositionedModelOwnerController,
  ReportParameterValue as BirdReportParameterValue,
  DataVisualElementController,
  SectionController,
} from '@sas-dvr/ltjs-bird';
import { TransportState } from '@sas-dvr/internal-va-react-core/store/transport/transportState';
import { embeddedBundle } from '@sas-dvr/internal-va-react-core/locale/embeddedI18N';
import {
  WarningLevel,
  FormatterFactory,
  EventDispatcher,
  WarningEvent,
  PortableEvent,
  SelectionController,
} from '@sas-dvr/ltjs-commons';
import {
  jsDateToSASDate,
  jsDateToSASDateTime,
  jsDateToSASTime,
} from '@sas-dvr/ltjs-sasicu/util/CalendarUtil';
import { SASReport } from '../transport/SASReport';
import { subscribeReport } from '../transport/SASReportManager';
import { getMediaContainerController, getControllerByName } from '../bird/util/controllerUtils';
import { takeUntil } from 'rxjs/operators';
import { ReplaySubject, combineLatest, BehaviorSubject } from 'rxjs';
import { ObjectHandle, ReportHandle } from '../handles';
import { exportPDF, ExportPDFOptions } from '../utils/printUtils';
import {
  exportData,
  getExportDataInfo,
  canExportData,
  ExportDataOptions,
  FormatType,
} from '../utils/exportDataUtils';
import { IReport, ReportReadyState } from '../transport/internal/IReport';
import { ltjsModule } from '@sas-dvr/ltjs-commons/ltjsEnvironment';
import { getWithUserState } from '../config';
import { AuthenticationType } from '@sas-dvr/internal-va-react-core/utils/authorization';
import { EpicActions } from '../bird/actions/epicActions';
import { formatValue } from '@sas-dvr/internal-va-react-core/components/utils/format';
import { ReportState } from '@sas-dvr/internal-va-react-core/store/rootReducer';

const genericError = embeddedBundle['SASReport.genericError.msg']();

/**
 * @public
 */
export type ReportParameterValue = string | number | Date;
/**
 * @public
 */
export type ReportParameters = {
  [keyof: string]: ReportParameterValue | ReportParameterValue[] | null | undefined;
};
export type ReportStore = ReturnType<typeof createStoreWithBirdMiddleware>;

/**
 * @public
 */
export type ReportObjectResultData = {
  resultName: string;
  rowCount: number;
  columns: {
    name: string;
    label: string;
    type: 'string' | 'number';
  }[];
  data: (string | number)[][];
};

const contexts = new Map<string, ReportContext>();

window.addEventListener('beforeunload', function () {
  if (!getWithUserState()) {
    return;
  }
  const closedReports = new Set<SASReport>();
  for (const [_, context] of contexts) {
    // ts-ignore because using private api
    //@ts-ignore
    if (!context._isFullReport) {
      continue;
    }
    //@ts-ignore
    const report: IReport | null = context._report;
    //@ts-ignore
    const session: ReportSession = context._session;
    if (session && report instanceof SASReport && !closedReports.has(report)) {
      closedReports.add(report);
      session.savePrimaryState();
    }
  }
});

function getKey(report: IReport, contextKey: string | undefined) {
  return `${getKeyPrefix(report.identifierOrLocation, report.getServer().getUrl())}:${contextKey}`;
}

function getKeyPrefix(reportUri: string, url: string) {
  return `${reportUri}:${url}`;
}

function containsOrIsContainedByAnotherObject(
  reportController: ReportController,
  objectName: string,
  currentViews: string[]
) {
  const sectionControllers = reportController.getAllSectionControllers();
  if (!sectionControllers) {
    return false;
  }

  for (const section of sectionControllers) {
    const mediaContainerController = getMediaContainerController(section);
    for (const view of currentViews) {
      // Check if the object is contained in the view
      const childController = getControllerByName(mediaContainerController, view);
      if (childController instanceof PositionedModelOwnerController) {
        const foundObjectName = !!getControllerByName(childController, objectName);
        if (foundObjectName) {
          return true;
        }
      }

      // Check if the object contains the view
      const object = getControllerByName(mediaContainerController, objectName);
      if (object instanceof PositionedModelOwnerController) {
        const foundObjectName = !!getControllerByName(object, view);
        if (foundObjectName) {
          return true;
        }
      }
    }
  }

  return false;
}

/**
 * Return the section controller if objectName is a section index or the name is
 * the name of a section. Otherwise undefined is returned.
 */
function getSectionForName(
  objectName: number | string,
  reportController: ReportController
): SectionController | undefined {
  const sections = reportController.getAllSectionControllers();
  if (!sections) {
    return undefined;
  }
  if (typeof objectName === 'number') {
    return sections[objectName] ?? undefined;
  }
  const section = sections.find((section) => {
    if (!section) {
      return false;
    }
    const model = section.getModel();
    if (model && model.getName() === objectName) {
      return true;
    }
    return false;
  });
  return section ?? undefined;
}

function hasWarningWithType(warningEvent: PortableEvent, types: number[]) {
  if (!(warningEvent instanceof WarningEvent)) {
    return false;
  }
  const warning = warningEvent.getWarnings()?.find((warning) => {
    const type = warning?.getWarningType();
    if (type === undefined) {
      return false;
    }
    return types.includes(type);
  });
  return !!warning;
}

type ReportStoreState = {
  reportAddedToStore: boolean;
  store: ReportStore;
  objectName?: string | number;
};

export class ReportContext {
  private _report: IReport | null = null;
  @ltjsProperty
  private _session: ReportSession | null = null;
  private _retainCount = 0;
  @ltjsProperty
  private _viewSet: ViewSet | null = null;
  private _rawViews: (string | number)[] | undefined;
  private _isFullReport: boolean;
  private _contextKey: string | undefined;
  private _destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  private _viewSet$ = new BehaviorSubject<(string | number)[]>([]);
  private _graphCSS: string | undefined;
  private _parameters?: ReportParameters;
  private _dateParameterTypes: { [keyof: string]: 'date' | 'time' | 'datetime' | undefined } = {};
  private _handle: ReportHandle;
  private _sessionStatus$ = new BehaviorSubject<'initial' | 'ready' | 'error'>('initial');
  private _reportStoreStates: Map<ReportStore, ReportStoreState> = new Map();

  constructor(
    report: IReport,
    isFullReport: boolean,
    contextKey: string | undefined,
    graphCSS?: string
  ) {
    this._contextKey = contextKey;
    this._report = report.retain();
    this._isFullReport = isFullReport;
    this._graphCSS = graphCSS;
    if (!this._isFullReport) {
      // this will make sure that at the time we call createSession
      // we will set a viewSet on the report configuration, ensuring
      // that we are in "viewSet mode"
      this._rawViews = [];
    }
    this._handle = new ReportHandle(this);

    combineLatest([report.getReportStateNotifier(), this._viewSet$])
      .pipe(takeUntil(this._destroyed$))
      .subscribe(([ready, viewSet]) => {
        this._reportStoreStates.forEach((state) => {
          this._processStore(state, ready, viewSet);
        });
        if (ready === 'loading' && this._sessionStatus$.getValue() !== 'initial') {
          this._session?.closeReport();
          this._session = null;
          this._sessionStatus$.next('initial');
        }
      });

    report
      .getErrorNotifier()
      .pipe(takeUntil(this._destroyed$))
      .subscribe((error) => {
        this._reportStoreStates.forEach(({ store }) => {
          store.dispatch(TransportState.actions.setTransportError(error));
        });
      });

    report
      .getServer()
      .getStatusNotifier()
      .pipe(takeUntil(this._destroyed$))
      .subscribe((status) => {
        this._reportStoreStates.forEach(({ store }) => {
          store.dispatch(TransportState.actions.setConnectionStatus(status));
        });
      });

    this.getSessionStatusNotifier()
      .pipe(takeUntil(this._destroyed$))
      .subscribe((status) => {
        if (status === 'error') {
          this._reportStoreStates.forEach(({ store }) => {
            store.dispatch(TransportState.actions.setTransportError(genericError));
          });
        }
      });
  }

  private _processStore(
    state: ReportStoreState,
    ready: ReportReadyState,
    viewSet: (string | number)[]
  ) {
    const { store, reportAddedToStore, objectName } = state;
    if (ready === 'ready') {
      if (!reportAddedToStore && (objectName === undefined || viewSet.includes(objectName))) {
        this._addReportToStore(store, objectName);
        state.reportAddedToStore = true;
      }
    } else if (ready === 'loading') {
      const controller = this._session?.getReportController();
      if (reportAddedToStore && controller) {
        store.dispatch(EpicActions.actions.unloadBirdModel(controller));
        store.dispatch(TransportState.actions.setTransportErrorKey(undefined));
        store.dispatch(TransportState.actions.setTransportError(undefined));
        store.dispatch(TransportState.actions.setReportLoaded('reload'));
        store.dispatch(TransportState.actions.setThemeLoaded(false));
        store.dispatch(TransportState.actions.setReportContentComplete(false));
        state.reportAddedToStore = false;
      }
    } else {
      store.dispatch(TransportState.actions.setReportLoaded('reload'));
    }
  }

  private _getSession() {
    if (this._session) {
      return this._session;
    }
    if (!this._report) {
      console.error('ReportContext has unexpectedly been destroyed');
      return null;
    }
    if (this._report.getReportState() !== 'ready') {
      return;
    }
    if (this._rawViews && !this._viewSet) {
      this._viewSet = new ViewSet();
      // FIXME: this is unnecessary when the portable fix is pushed.
      this._viewSet.setIncluded(
        this._rawViews.filter((val) => typeof val === 'string') as string[]
      );
    }
    this._session = this._report.createSession(this._viewSet, this._graphCSS);
    if (!this._session) {
      console.error('unable to create Report Session');
      this._sessionStatus$.next('error');
      return;
    }
    this._synchronizeViews();
    const rc = this._session.getReportController();
    if (!rc) {
      console.error('unable to get ReportController');
      this._sessionStatus$.next('error');
      return;
    }
    // We may need to do some js Date to SAS date/time values, so go ahead and get the relaxant information
    //  from the formatters.
    const promptDefs = rc.getModel()?.getPromptDefinitions();
    if (promptDefs) {
      for (const def of promptDefs) {
        const formatName = def?.getFormat();
        const label = def?.getLabel();
        if (label && formatName) {
          const formatter = FormatterFactory.instance()?.getFormatterForFormatName(formatName);
          if (formatter) {
            if (formatter.isDateTimeFormat()) {
              this._dateParameterTypes[label] = 'datetime';
            } else if (formatter.isDateFormat()) {
              this._dateParameterTypes[label] = 'date';
            } else if (formatter.isTimeFormat()) {
              this._dateParameterTypes[label] = 'time';
            }
          }
        }
      }
    }
    // If parameters were set before report load, apply them now.
    if (this._parameters) {
      this.setParameters(this._parameters);
    }
    const warnings = rc.getWarnings();
    if (warnings) {
      for (const warning of warnings) {
        if (!warning) {
          continue;
        }
        if (warning.getLevel() === WarningLevel.FATAL_LEVEL) {
          console.error(warning.getMessage() || genericError);
          this._sessionStatus$.next('error');
          return;
        } else {
          console.warn('ReportContext: ', warning.getMessage());
        }
      }
    }
    this._sessionStatus$.next('ready');
    return this._session;
  }

  getSessionStatusNotifier() {
    return this._sessionStatus$;
  }

  getReportSession(): ReportSession | undefined {
    return this._getSession() ?? undefined;
  }

  getReportController(): ReportController | undefined {
    return this.getReportSession()?.getReportController() || undefined;
  }

  private findStoreState(objectName?: string) {
    for (const state of this._reportStoreStates.values()) {
      if (state.objectName === objectName) {
        return state;
      }
    }
    return undefined;
  }

  private calculateReadyState(type: 'report' | 'object', reportState?: ReportState) {
    if (!reportState || reportState.transport.transportError) {
      return 'error';
    }
    const isContentComplete =
      type === 'report'
        ? reportState?.transport.reportContentComplete
        : reportState?.transport.reportObjectContentComplete;

    if (reportState?.transport.reportLoaded === 'reload') {
      return 'loading';
    }
    if (reportState?.transport.reportLoaded) {
      if (isContentComplete) {
        return 'complete';
      } else {
        return 'contentLoading';
      }
    } else {
      return 'loading';
    }
  }

  getReadyState(objectName?: string): 'loading' | 'contentLoading' | 'complete' | 'error' {
    if (objectName) {
      const visualElementController = this.getReportController()?.getVisualElementControllerForVisualElementName(
        objectName
      );
      if (!visualElementController || !this._session) {
        return 'error';
      }
    }

    const store = this.findStoreState(objectName)?.store;
    if (!store) {
      return 'error';
    }
    const readyState = this.calculateReadyState(objectName ? 'object' : 'report', store.getState());
    if (!readyState || this.getSessionStatusNotifier().value === 'error') {
      return 'error';
    } else {
      return readyState;
    }
  }

  exportPDF(options?: ExportPDFOptions) {
    const reportSession = this._session;
    if (!reportSession) {
      throw new Error('Unable to get ReportSession');
    }
    return exportPDF(reportSession, options);
  }

  exportData(objectName: string, format: FormatType, options?: ExportDataOptions) {
    const visualElementController = this.getReportController()?.getVisualElementControllerForVisualElementName(
      objectName
    );

    if (!visualElementController) {
      throw new Error('The requested object name does not exist.');
    }
    return exportData(visualElementController, format, options);
  }

  getData(
    objectName: string,
    formatData: boolean | 'datesOnly' = 'datesOnly',
    selected: boolean
  ): ReportObjectResultData[] {
    const visualElementController = this.getReportController()?.getVisualElementControllerForVisualElementName(
      objectName
    );
    if (
      !visualElementController ||
      !(visualElementController instanceof DataVisualElementController)
    ) {
      throw new Error(`Visual element not found for object name: ${objectName}`);
    }

    const dataSets = selected
      ? visualElementController.getDataSetsForSelections()
      : visualElementController.getAllDataSets();
    if (!dataSets || !dataSets.length) {
      return [];
    }

    const selectedResults: ReportObjectResultData[] = [];
    for (const dataSet of dataSets) {
      const columns = [];
      const data: (string | number)[][] = [];
      const dataSetColumns = dataSet?.getColumns();
      const dataSetName = dataSet?.getName();
      if (!dataSetColumns || !dataSetColumns.length || !dataSetName) {
        continue;
      }
      for (const column of dataSetColumns) {
        const name = column?.getName();
        const label = column?.getLabel();
        const isDateTime = column?.isDateTime();
        const type: 'string' | 'number' = column?.getDataType() === 0 ? 'number' : 'string';
        const numberOfRows = column?.getNumberOfRows();

        if (!name || !label || !type || !numberOfRows) {
          continue;
        }
        for (let rowIndex = 0; rowIndex < numberOfRows; rowIndex++) {
          const dataValue = column?.getDataValueAtIndex(rowIndex, true);
          let value;
          if (isDateTime) {
            const numericValue = dataValue?.getNumericValue();
            const formatName = column?.getFormatName();
            if (!numericValue || !formatName) {
              continue;
            }
            value = formatData ? formatValue(formatName, numericValue) : numericValue;
          } else {
            if (formatData === true) {
              value = dataValue?.getFormattedValue();
            } else {
              value =
                type === 'number' ? dataValue?.getNumericValue() : dataValue?.getStringValue();
            }
          }
          if (!data[rowIndex]) {
            data[rowIndex] = [];
          }
          // Handles all empty values except for 0
          if (value === null || value === undefined) {
            data[rowIndex].push(type === 'number' ? NaN : '.');
          } else {
            data[rowIndex].push(value);
          }
        }
        columns.push({ name, label, type });
      }

      if (data.length > 0) {
        selectedResults.push({
          resultName: dataSetName,
          rowCount: data.length,
          columns,
          data,
        });
      }
    }
    return selectedResults;
  }

  subscribeEvent(
    type: 'readyStateChanged' | 'selectionChanged',
    event:
      | {
          type: 'object';
          callback: (event: { type: string; target: ObjectHandle }) => void;
          objectHandle: ObjectHandle;
        }
      | {
          type: 'report';
          callback: (event: { type: string; target: ReportHandle }) => void;
          reportHandle: ReportHandle;
        }
  ) {
    if (type === 'readyStateChanged') {
      const store = this.findStoreState(
        event.type === 'object' ? event.objectHandle.objectName : undefined
      )?.store;
      if (!store) {
        return;
      }
      let readyState = this.calculateReadyState(event.type, store.getState());
      if (event.type === 'object' && (readyState === 'complete' || readyState === 'error')) {
        return;
      }
      const unsubscribe = store.subscribe(() => {
        const newReadyState = this.calculateReadyState(event.type, store.getState());
        if (
          event.type === 'object' &&
          (newReadyState === 'complete' || newReadyState === 'error')
        ) {
          unsubscribe();
        }
        if (readyState !== newReadyState) {
          if (event.type === 'object') {
            event.callback({ type, target: event.objectHandle });
          } else {
            event.callback({ type, target: event.reportHandle });
          }
          readyState = newReadyState;
        }
      });
      return { detach: unsubscribe };
    } else if (event.type === 'object' && type === 'selectionChanged') {
      // Passing the event to the callback before sending it to portable's attachEvent
      const portableEventCallback = () => {
        event.callback({ type, target: event.objectHandle });
      };
      const eventDetachHandle = this.getReportController()
        ?.getVisualElementControllerForVisualElementName(event.objectHandle.objectName)
        ?.attachEvent(SelectionController.SELECTION_CHANGED, portableEventCallback);
      if (eventDetachHandle) {
        return eventDetachHandle;
      }
    }
  }

  getExportDataInfo(objectName: string) {
    const visualElementController = this.getReportController()?.getVisualElementControllerForVisualElementName(
      objectName
    );

    if (!visualElementController) {
      throw new Error('The requested object name does not exist.');
    }
    return getExportDataInfo(visualElementController);
  }

  canExportData(objectName: string) {
    const visualElementController = this.getReportController()?.getVisualElementControllerForVisualElementName(
      objectName
    );

    if (!visualElementController) {
      return false;
    }
    return canExportData(visualElementController);
  }

  async saveUserStateXML() {
    const manager = this._session?.getReportManager();
    const report = this._session?.getReport();
    const controller = this._session?.getReportController();
    if (!manager) {
      throw new Error('Unable to get ReportManager');
    }
    if (!report) {
      throw new Error('Unable to get Report');
    }
    if (!controller) {
      throw new Error('Unable to get ReportController');
    }
    controller.saveUserState();
    const reportPath = this._session
      ?.getSASReportConfiguration()
      ?.getFullyQualifiedPathToBIRDReportDirectory();

    return new Promise<string>((resolve) => {
      resolve(ltjsModule.getStringContentsOfFile(`${reportPath}/userStateReport.xml`)!!);
    });
  }

  initializeStore(objectName?: string | number): ReportStore {
    const store = createStoreWithBirdMiddleware();
    const server = this.report?.getServer();

    if (!server || !this.report) {
      store.dispatch(
        TransportState.actions.setTransportError(embeddedBundle['SASServer.genericError.msg']())
      );
      return store;
    }

    // We are assuming that if this context is to be used by multiple objects
    // instead of a full report that an objectName will be provided here.
    // Also block multiple instances of the same object within a ViewSet.
    if (
      (this._isFullReport && objectName !== undefined) || // this context applies to a full report, not an object
      (!this._isFullReport && objectName === undefined) || // this context is already been used for single objects
      (this._rawViews && objectName !== undefined && this._rawViews.indexOf(objectName) !== -1) // This object is already in use
    ) {
      store.dispatch(
        TransportState.actions.setTransportErrorKey({
          key: 'ReportObject.multipleReferences.error.txt',
        })
      );
      return store;
    }

    const state: ReportStoreState = {
      reportAddedToStore: false,
      objectName,
      store,
    };
    this._reportStoreStates.set(store, state);
    this._processStore(state, this.report.getReportState(), this._rawViews || []);

    return store;
  }

  destroyStore(store: ReportStore) {
    const reportController = this.getReportController();
    if (reportController) {
      store.dispatch(EpicActions.actions.unloadBirdModel(reportController));
    }
    this._reportStoreStates.delete(store);
  }

  private _addReportToStore(store: ReportStore, objectName: string | number | undefined) {
    const session = this._getSession();
    if (!session) {
      return;
    }
    const server = this.report?.getServer();
    if (!this.report || !server) {
      store.dispatch(TransportState.actions.setTransportError(genericError));
      console.error('Server not found');
      return;
    }
    const reportController = session.getReportController();
    if (!reportController) {
      store.dispatch(TransportState.actions.setTransportError(genericError));
      console.error('Unable to get ReportController');
      return;
    }
    if (objectName !== undefined) {
      let resolvedName = objectName;
      if (typeof resolvedName === 'number') {
        const name = this._getNameForSectionIndex(resolvedName);
        if (!name) {
          store.dispatch(
            TransportState.actions.setTransportErrorKey({
              key: 'ReportObject.invalidPage.error.txt',
            })
          );
          console.error(`Page index ${objectName} is out of range`);
          return;
        }
        resolvedName = name;
      }
      store.dispatch(BirdState.actions.setViewSetReport(true));
      const items = this._viewSet ? this._viewSet.getIncluded() : null;
      if (!items) {
        store.dispatch(TransportState.actions.setTransportError(genericError));
        console.error('viewSet returned null');
        return;
      }
      // It may not be immediately obvious what this check is doing.  Since the items that are in this view set
      //  has been through the _synchronizeViews() code which filters out duplicates, parents, and siblings of
      //  previous items in the _rawViews array if this resolved name is not in the items array it means that
      //  it cannot be displayed because it is already being rendered by another view.
      if (!items.includes(resolvedName)) {
        store.dispatch(
          TransportState.actions.setTransportErrorKey({
            key: 'ReportObject.multipleReferences.error.txt',
          })
        );
        console.error('Report objects cannot be used in multiple contexts.');
        return;
      }
      const section = getSectionForName(objectName, reportController);
      if (section !== undefined) {
        // Set the controller to the correct section before loading Bird models.
        // TODO: we never release if CURRENT_SECTION_CHANGED isn't called. Small chance for a memory leak.
        if (reportController.getCurrentSectionController() !== section) {
          reportController.attachEventOnce(ReportController.CURRENT_SECTION_CHANGED, () => {
            reportController.popHiddenSection();
            reportController.release();
            this._finalizeStore(store, reportController);
          });
          reportController.retain();
          reportController.setCurrentSectionIndex(section.getSectionIndex());
        } else {
          this._finalizeStore(store, reportController);
        }
      } else {
        store.dispatch(BirdState.actions.setReportObjectName(resolvedName));
        this._finalizeStore(store, reportController);
      }
    } else {
      this._finalizeStore(store, reportController);
    }
  }

  private _finalizeStore(store: ReportStore, reportController: ReportController) {
    if (this.report) {
      const server = this.report.getServer();
      store.dispatch(TransportState.actions.setServerType(server.getServerType()));
      let hostUrl = server.getUrl();
      if (hostUrl.endsWith('/')) {
        hostUrl = hostUrl.slice(0, -1);
      }
      store.dispatch(TransportState.actions.setServerHost(hostUrl));
      // NOTE: this is currently only used in standalone mode.
      let url = `${hostUrl}/${this.report.identifierOrLocation}`;
      if (!url.endsWith('/')) {
        url += '/';
      }
      store.dispatch(TransportState.actions.setPackageUrl(url));
      store.dispatch(TransportState.actions.setIframeSandbox(server.getIframeSandbox()));
    }
    store.dispatch(BirdState.actions.setReportLabel(reportController.getModel()?.getLabel() || ''));
    store.dispatch(EpicActions.actions.loadBirdModel(reportController));
    store.dispatch(TransportState.actions.setReportLoaded(true));
  }

  addToViewSet(objectName: string | number | undefined) {
    if (objectName === undefined) {
      return;
    }

    if (!this._rawViews) {
      this._rawViews = [];
    }
    this._rawViews.push(objectName);
    this._synchronizeViews();

    this._viewSet$.next(this._rawViews);
  }

  removeFromViewSet(objectName: string | number | undefined) {
    if (objectName === undefined) {
      return;
    }

    if (this._rawViews) {
      const initialIndex = this._rawViews.indexOf(objectName);
      if (initialIndex !== -1) {
        this._rawViews.splice(initialIndex, 1);
        this._synchronizeViews();
        this._viewSet$.next(this._rawViews);
      }
    }
  }

  private _synchronizeViews() {
    if (!this._session || !this._rawViews) {
      return;
    }
    const rc = this._session.getReportController();
    if (!rc) {
      return;
    }
    const resolvedViews = this._rawViews
      .map((value) => (typeof value === 'number' ? this._getNameForSectionIndex(value) : value))
      .reduce((accumulator, value) => {
        if (!value || containsOrIsContainedByAnotherObject(rc, value, accumulator)) {
          return accumulator;
        }
        accumulator.push(value);
        return accumulator;
      }, [] as string[]);
    if (!this._viewSet) {
      this._viewSet = new ViewSet();
    }
    this._viewSet.setIncluded(resolvedViews);
  }

  private _getNameForSectionIndex(objectName: number) {
    if (!this._session) {
      return;
    }
    const rc = this._session.getReportController();
    if (!rc) {
      return;
    }
    const sections = rc.getAllSectionControllers();
    if (!sections) {
      return;
    }
    const section = sections[objectName];
    if (!section) {
      return;
    }
    const model = section.getModel();
    if (!model) {
      return;
    }
    const name = model.getName();
    if (name) {
      return name;
    }
  }

  get report() {
    return this._report;
  }

  get url() {
    if (this._report) {
      return this._report.getServer().getUrl();
    } else {
      return '';
    }
  }

  getParameters() {
    return this._parameters;
  }

  setParameters(parameters: ReportParameters = {}) {
    const oldParameters = this._parameters ?? {};
    this._parameters = parameters;
    const controller = this._session?.getReportController();
    if (!controller) {
      return;
    }

    // Update the parameters present in the object
    this.updateParameters(parameters);

    // Reset all other parameters to their initial value
    const resetKeys = [];
    const updatedKeys = Object.keys(parameters);
    const initialParams = filterNulls(controller.getInitialReportParameterValues());
    for (const initialParam of initialParams) {
      const key = initialParam.getLabel();
      if (key && !updatedKeys.includes(key)) {
        resetKeys.push(key);
        controller.setReportParameterValue(initialParam);
      }
    }

    // Reset any parameters that were not updated and not present in initialParams
    for (const key of Object.keys(oldParameters)) {
      if (!updatedKeys.includes(key) && !resetKeys.includes(key)) {
        const param = new BirdReportParameterValue();
        param.setLabel(key);
        param.setValues(null);
        controller.setReportParameterValue(param);
      }
    }
  }

  updateParameters(parameters: ReportParameters) {
    this._parameters = { ...this._parameters, ...parameters };
    const controller = this._session?.getReportController();
    if (!controller) {
      return;
    }
    for (const key of Object.keys(parameters)) {
      const value = parameters[key];
      const param = new BirdReportParameterValue();
      param.setLabel(key);
      if (value === null || value === undefined || value === '') {
        param.setValues(null);
      } else if (this._dateParameterTypes[key]) {
        const vals = Array.isArray(value) ? value : [value];
        const dateType = this._dateParameterTypes[key];
        param.setValues(
          vals.map((val) => {
            if (typeof val === 'string' || typeof val === 'number') {
              return val + '';
            }
            switch (dateType) {
              case 'datetime':
                return jsDateToSASDateTime(val) + '';
              case 'date':
                return jsDateToSASDate(val) + '';
              case 'time':
                return jsDateToSASTime(val) + '';
            }
            return null;
          })
        );
      } else {
        param.setValues(Array.isArray(value) ? value.map((v) => v + '') : [value + '']);
      }
      controller.setReportParameterValue(param);
    }
  }

  getReportHandle(): ReportHandle {
    return this._handle;
  }

  private _saveState(destroySelf: boolean) {
    if (!(this.report instanceof SASReport) || !this._session) {
      return;
    }
    this.report.setUserStateSaving(true);
    const handle = this._session.attachEvent(EventDispatcher.ALL, (event) => {
      const eventType = event.getType();
      if (eventType === ReportSession.STATE_SAVED) {
        handle.detach();
        const report = this.report;
        if (destroySelf) {
          this._destroy();
        }
        if (report instanceof SASReport) {
          report.setUserStateSaving(false);
        }
      } else if (
        eventType === WarningEvent.GENERIC &&
        hasWarningWithType(event, [
          ReportSession.STATE_NOT_SAVED,
          ReportSession.STATE_NOT_SUPPORTED,
        ])
      ) {
        handle.detach();
        if (this._report) {
          console.error(
            `Failed to save user state for report ${getKeyPrefix(
              this._report.identifierOrLocation,
              this._report.getServer().getUrl()
            )}`
          );
        }
        const report = this.report;
        if (destroySelf) {
          this._destroy();
        }
        if (report instanceof SASReport) {
          report.setUserStateSaving(false);
        }
      }
    });
    this._session.savePrimaryState();
  }

  reloadReport(ignoreUserState?: true) {
    if (!(this.report instanceof SASReport)) {
      console.error('Refresh only supported for server connected reports.');
      return;
    }
    if (!ignoreUserState && getWithUserState()) {
      // _saveState refreshes the report
      this._saveState(false);
    } else {
      this.report.reload(ignoreUserState);
    }
  }

  retain() {
    this._retainCount++;
    return this;
  }

  release() {
    if (this._retainCount <= 0) {
      throw new Error('ReportContext released without being retained');
    }
    this._retainCount--;
    if (this._retainCount === 0) {
      // Only save state with full reports to keep things simple for  now.
      if (
        this._report &&
        this._session &&
        getWithUserState() &&
        this._isFullReport &&
        this.report instanceof SASReport
      ) {
        contexts.delete(getKey(this._report, this._contextKey));
        this.report.setUserStateSaving(true);
        this._saveState(true);
      } else {
        this._destroy();
      }
    }
  }

  @ltjsDestructor
  private _destroy() {
    this._handle.destroy();
    if (this._report) {
      contexts.delete(getKey(this._report, this._contextKey));
      this._report.release();
      this._report = null;
      if (this._session) {
        this._session.closeReport();
      }
      this._session = null;
      this._viewSet = null;
    }
    this._destroyed$.next(true);
    this._sessionStatus$.complete();
    this._reportStoreStates.clear();
  }
}

export type getReportContextOptions = (
  | {
      url: string;
      reportUri: string;
      authenticationType: AuthenticationType;
    }
  | {
      packageUri: string;
    }
) & {
  isFullReport?: boolean;
  contextKey?: string;
  graphCSS?: string;
};

export function getReportContext(props: getReportContextOptions) {
  const { isFullReport, contextKey, graphCSS, ...reportOpts } = props;
  const report = subscribeReport(reportOpts);
  let context = contexts.get(getKey(report, props.contextKey));
  if (!context) {
    context = new ReportContext(report, isFullReport ?? true, contextKey, graphCSS);
    contexts.set(getKey(report, contextKey), context);
  }

  return context;
}

export function getMatchingReportContext(url: string, reportUri: string) {
  for (const [key, context] of contexts.entries()) {
    const keyPrefix = getKeyPrefix(reportUri, url);
    if (key.startsWith(keyPrefix)) {
      return context;
    }
  }
  return null;
}
